import { useRef, useEffect, useCallback } from 'react';

export function useDebouncedCallback<T extends (...args: never[]) => unknown>(
  callback: T,
  delay: number = 300,
  dependencies: ReadonlyArray<unknown> = [],
): T {
  const debouncedFunction = useRef<T>(callback);
  debouncedFunction.current = callback;

  const isComponentUnmounted = useRef(false);
  const functionTimeoutHandler = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      isComponentUnmounted.current = true;
    },
    [],
  );

  // oxlint-disable-next-line react/exhaustive-deps
  return useCallback(
    ((...args) => {
      if (functionTimeoutHandler.current) {
        clearTimeout(functionTimeoutHandler.current);
      }

      functionTimeoutHandler.current = setTimeout(() => {
        functionTimeoutHandler.current = null;

        if (!isComponentUnmounted.current) {
          debouncedFunction.current(...args);
        }
      }, delay);
    }) as T,
    // oxlint-disable-next-line react/exhaustive-deps
    [delay, ...dependencies],
  );
}
